/**
 * DeleteAccountModal — destructive confirm for account deletion.
 *
 * Phase 3 Wave 4 — LEGAL-07 (right to erasure).
 *
 * A single explicit confirm (warning + "Delete my account" / "Cancel"). The
 * earlier "type DELETE to confirm" gate was removed — it's a developer-tool
 * pattern that's heavy/unfriendly for a consumer app; a clear two-button modal
 * (with backdrop/ESC dismiss) is sufficient friction for a deliberate action.
 *
 * Design:
 *   - candy_heart shell cloned from SignOutConfirmModal (AccountMenu.jsx)
 *   - No 1px borders — tonal surface + ambient shadow (candy_heart No-Line Rule)
 *   - zIndex 340 — sits above AccountMenu dropdown
 *   - Inline styles only (monolith architecture per CLAUDE.md)
 *
 * Props:
 *   open            {boolean}  — whether the modal is visible
 *   onClose         {function} — called on cancel, ESC, or backdrop click
 *   onConfirmDelete {function} — async; called once when the user confirms.
 *                                If it rejects, an error shows and the modal stays open.
 */
import { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";

export default function DeleteAccountModal({ open, onClose, onConfirmDelete }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // ESC dismiss — matches PaywallModal pattern
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [open, onClose]);

  // Reset transient state on every open
  useEffect(() => {
    if (open) {
      setBusy(false);
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await onConfirmDelete();
    } catch (err) {
      setError(err.message || t("ui.deleteAccount.genericError"));
    } finally {
      setBusy(false);
    }
  };

  // ── Styles (candy_heart spec, cloned from SignOutConfirmModal) ──
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    zIndex: 340,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  };
  const cardStyle = {
    background: "var(--color-surface)",
    borderRadius: "2rem",
    padding: 32,
    maxWidth: 420,
    width: "100%",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    position: "relative",
  };
  const titleStyle = {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    fontWeight: 800,
    color: "var(--color-text)",
    margin: "0 0 12px",
    textAlign: "center",
  };
  const bodyStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 14,
    color: "var(--color-textMuted)",
    lineHeight: 1.6,
    margin: "0 0 24px",
    textAlign: "center",
  };
  const ctaPrimary = {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: 16,
    fontFamily: "var(--font-body)",
    fontSize: 16,
    fontWeight: 700,
    cursor: busy ? "not-allowed" : "pointer",
    background: "linear-gradient(135deg, #c0392b, #e74c3c)",
    color: "#ffffff",
    boxShadow: "0 8px 24px rgba(192,57,43,0.28)",
    opacity: busy ? 0.6 : 1,
    transition: "opacity 0.15s ease, box-shadow 0.15s ease",
  };
  const ctaSecondary = {
    ...ctaPrimary,
    background: "var(--color-bgAlt)",
    color: "var(--color-text)",
    boxShadow: "none",
    opacity: 1,
    cursor: "pointer",
  };
  const errorStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 13,
    color: "var(--color-wrong, #c0392b)",
    textAlign: "center",
    margin: "0 0 12px",
  };

  return (
    <div
      onClick={onClose}
      style={overlayStyle}
      data-testid="delete-account-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-label={t("ui.deleteAccount.confirmAria")}
      >
        {/* Icon */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 20,
            margin: "0 auto 16px",
            background: "linear-gradient(135deg, #c0392b, #e74c3c)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            boxShadow: "0 8px 24px rgba(192,57,43,0.28)",
          }}
          aria-hidden="true"
        >
          🗑️
        </div>

        <h2 style={titleStyle}>{t("ui.deleteAccount.title")}</h2>

        <p style={bodyStyle}>
          <Trans i18nKey="ui.deleteAccount.body" components={{ strong: <strong /> }} />
        </p>

        {/* Error message */}
        {error && (
          <p style={errorStyle} data-testid="delete-account-error">
            {error}
          </p>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            style={ctaPrimary}
            data-testid="delete-account-confirm-btn"
          >
            {busy ? t("ui.deleteAccount.confirmBusy") : t("ui.deleteAccount.confirm")}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={ctaSecondary}
            data-testid="delete-account-cancel-btn"
          >
            {t("ui.deleteAccount.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
